/**
 * ProfilePicture Component
 *
 * A robust profile picture component with error handling, fallback, and loading states.
 * Handles broken image URLs gracefully and provides consistent user experience.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../config';
import iconUser from '../assets/icon-user.svg';
import './ProfilePicture.css';

const ProfilePicture = ({
  profilePictureUrl,
  firstName,
  lastName,
  email,
  size = 'medium',
  className = '',
  showInitials = true
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Reset image error and loading states when the URL changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [profilePictureUrl]);

  /**
   * Get user initials for fallback display
   */
  const getInitials = () => {
    if (firstName) {
      return firstName.charAt(0).toUpperCase() +
        (lastName ? lastName.charAt(0).toUpperCase() : '');
    }
    return email?.charAt(0).toUpperCase() || 'U';
  };

  /**
   * Construct full image URL with validation
   */
  const getImageUrl = () => {
    if (!profilePictureUrl) return null;

    // If URL is already absolute, use it as-is
    if (profilePictureUrl.startsWith('http://') || profilePictureUrl.startsWith('https://')) {
      return profilePictureUrl;
    }

    // If URL starts with /api, use API_BASE_URL's origin only
    if (profilePictureUrl.startsWith('/api')) {
      try {
        const baseUrl = new URL(API_BASE_URL);
        return `${baseUrl.origin}${profilePictureUrl}`;
      } catch (error) {
        console.error('Invalid API_BASE_URL:', error);
        return null;
      }
    }

    // Otherwise, prepend API_BASE_URL
    return `${API_BASE_URL}${profilePictureUrl}`;
  };

  /**
   * Handle image load error
   */
  const handleImageError = () => {
    console.warn('Failed to load profile picture:', profilePictureUrl);
    setImageError(true);
    setImageLoading(false);
  };

  /**
   * Handle successful image load
   */
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const imageUrl = getImageUrl();
  const sizeClass = `profile-picture--${size}`;
  const shouldShowImage = imageUrl && !imageError;

  return (
    <div className={`profile-picture ${sizeClass} ${className}`}>
      {shouldShowImage ? (
        <>
          <img
            key={imageUrl}
            src={imageUrl}
            alt={`${firstName || email}'s profile`}
            className={`profile-picture__image ${imageLoading ? 'profile-picture__image--loading' : ''}`}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
          {imageLoading && (
            <div className="profile-picture__loading">
              <div className="profile-picture__spinner" />
            </div>
          )}
        </>
      ) : showInitials ? (
        <div className="profile-picture__initials">
          {getInitials()}
        </div>
      ) : (
        <img
          src={iconUser}
          alt="Default profile"
          className="profile-picture__default-icon"
        />
      )}
    </div>
  );
};

ProfilePicture.propTypes = {
  profilePictureUrl: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'xlarge']),
  className: PropTypes.string,
  showInitials: PropTypes.bool
};

export default ProfilePicture;
